'use client';

import React, { memo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from '@/src/i18n/navigation';
import { Menu } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { css, cx } from '@/styled-system/css';
import { token } from '@/styled-system/tokens';
import HamburgerMenu from './HamburgerMenu';
import HotPadsLogo from '@/src/components/ui/icons/HotPadsLogo';
import { LucideIcon } from '@/src/components/LucideIcon';
import { UserDrawer } from '@/src/components/UserDrawer';
import { IconButton } from '@zillow/constellation-10';
import { useIsDesktop } from '@/src/lib/hooks';
import { useHeaderVisibility } from '@/src/layouts/HeaderVisibilityContext';

const hiddenOnMobile = css({ display: { base: 'none', lg: 'block' } });

/**
 * Outer shell positioning. The homepage variant absolutely positions the nav so it sits over
 * the hero illustration instead of taking its own row. Single conditional `css({})` for the
 * same Panda-cascade reason as `headerSurfaceClass` — atomic `position` classes from two
 * separate styles risked having `position: relative` win over `position: absolute` when the
 * cssgen scope changed.
 */
const headerShellClass = (isHomepageOverlay: boolean) =>
  css({
    position: isHomepageOverlay ? 'absolute' : 'relative',
    top: isHomepageOverlay ? 0 : undefined,
    right: isHomepageOverlay ? 0 : undefined,
    left: isHomepageOverlay ? 0 : undefined,
    overflow: isHomepageOverlay ? 'visible' : undefined,
    zIndex: 'zIndexParent',
  });

/**
 * The header's background surface. `'default'` lays the chrome over `bg.neutral`; the homepage
 * variant goes transparent so the hero illustration shows through. Collapsed into a single
 * conditional `css({})` instead of `cx(headerSurface, isHomepageOverlay && homepageOverlaySurface)`
 * so the resulting className carries exactly one `background-color` atomic — earlier the
 * `cx`-of-two-atoms shape was vulnerable to Panda extraction-order shuffling (when the cssgen
 * scope grew to include the satellite-inbox module, the bg.neutral atom landed after the
 * transparent atom in the stylesheet and won the cascade on the homepage).
 */
const headerSurfaceClass = (isHomepageOverlay: boolean) =>
  css({
    backgroundColor: isHomepageOverlay ? 'transparent' : 'bg.neutral',
  });

const desktopOnlyBlock = css({ display: { base: 'none', lg: 'block' } });

const desktopCenterSlot = css({
  display: { base: 'none', lg: 'flex' },
  alignItems: 'center',
  justifyContent: 'center',
  flex: '1 1 auto',
  minWidth: 0,
});

const HAMBURGER_MENU_DIALOG_ID = 'header-navigation-menu';

type HeaderNavProps = {
  appearance?: 'default' | 'homepageOverlay';
  desktopCenterSlot?: ReactNode;
};

function HeaderNav({ appearance = 'default', desktopCenterSlot: desktopCenterSlotContent }: HeaderNavProps = {}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { hideHeaderOnMobile } = useHeaderVisibility();
  const isDesktop = useIsDesktop();
  const t = useTranslations('nav');

  const shouldHideOnMobile = hideHeaderOnMobile;
  const isHomepageOverlay = appearance === 'homepageOverlay';

  return (
    <>
      <div
        className={cx(headerShellClass(isHomepageOverlay), shouldHideOnMobile && hiddenOnMobile)}
        data-appearance={appearance}
      >
        <div className={headerSurfaceClass(isHomepageOverlay)}>
          <header
            className={css({
              height: { base: 'auto', lg: 'auto' },

              paddingRight: 0,
              display: 'flex',
              alignItems: 'center',
            })}
          >
            <nav
              className={css({
                width: '100%',
                display: 'flex',
                flexDirection: { base: 'column', lg: 'row' },
                alignItems: { base: 'stretch', lg: 'center' },

                gap: { base: 'tighter', lg: 'tighter' },
                paddingBlock: { base: 'tighter', lg: 'loose' },
                paddingInline: 'default',
              })}
              aria-label="main"
            >
              {/* Row 1 on mobile, left cluster on desktop: hamburger + logo + user drawer */}
              <div
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: { base: 'space-between', lg: 'flex-start' },
                  width: { base: '100%', lg: 'auto' },

                  flexShrink: 0,
                  gap: 'tighter',
                })}
              >
                <div className={css({ display: 'flex', alignItems: 'center', gap: 'tightest' })}>
                  <IconButton
                    tone={isDesktop ? 'brand' : 'neutral'}
                    emphasis="bare"
                    shape="circle"
                    size="sm"
                    title={t('navigationMenu')}
                    icon={<LucideIcon icon={Menu} color={token.var('colors.text.neutral')} />}
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    aria-controls={HAMBURGER_MENU_DIALOG_ID}
                    aria-expanded={isMenuOpen}
                    aria-haspopup="dialog"
                  />

                  <Link
                    href="/"
                    className={css({
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    })}
                    aria-label={t('hotpadsHome')}
                  >
                    <HotPadsLogo
                      width={114}
                      height={30}
                      className={css({
                        cursor: 'pointer',
                        display: 'block',
                        color: {
                          _light: 'text.subtle',
                          _dark: 'text.neutral',
                        },
                      })}
                    />
                  </Link>
                </div>

                {/* Mobile-only: UserDrawer sits at the end of row 1 */}
                <div
                  className={css({
                    display: { base: 'flex', lg: 'none' },
                    alignItems: 'center',
                    flexShrink: 0,
                  })}
                >
                  <UserDrawer />
                </div>
              </div>

              {desktopCenterSlotContent ? (
                <div className={desktopCenterSlot}>{desktopCenterSlotContent}</div>
              ) : (
                <div className={cx(desktopOnlyBlock, css({ flex: 1 }))} />
              )}

              {/* Desktop: UserDrawer is the sole right-side element */}
              <div
                className={css({
                  display: { base: 'none', lg: 'flex' },
                  alignItems: 'center',
                  flexShrink: 0,
                  marginLeft: desktopCenterSlotContent ? 0 : 'auto',
                })}
              >
                <UserDrawer />
              </div>
            </nav>
          </header>
        </div>
      </div>

      <HamburgerMenu id={HAMBURGER_MENU_DIALOG_ID} isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />

      <div id="skip-topnav-target" tabIndex={-1} />
    </>
  );
}

export default memo(HeaderNav);
